/**
 * ImpactGrid — Scrollable table of impacted assets with expandable dependency chains
 */

import { useState } from 'react'
import { DOMAIN_CONFIG } from '../../../../types'
import type { TimePoint, AssetCascadeResult } from '../../../../services/cascadeEngine'

interface ImpactGridProps {
  results: AssetCascadeResult[]
  currentTime: TimePoint
}

const STATUS_STYLES: Record<string, { label: string; color: string; bg: string }> = {
  NOMINAL: { label: 'NOMINAL', color: 'text-emerald-400', bg: 'bg-emerald-500/15 border-emerald-500/40' },
  DEGRADED: { label: 'DEGRADED', color: 'text-amber-400', bg: 'bg-amber-500/15 border-amber-500/40' },
  SEVERELY_DEGRADED: { label: 'SEVERE', color: 'text-red-400', bg: 'bg-red-500/15 border-red-500/40' },
  MISSION_DENIED: { label: 'DENIED', color: 'text-red-400', bg: 'bg-red-500/20 border-red-500/50' },
}

function CapacityBar({ baseline, current, isLost }: { baseline: number; current: number; isLost: boolean }) {
  if (isLost) {
    return (
      <div className="flex items-center gap-1.5">
        <div className="flex gap-[2px]">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="w-[6px] h-3 rounded-[1px] bg-red-500/30" />
          ))}
        </div>
        <span className="text-[10px] text-red-400 font-bold">LOST</span>
      </div>
    )
  }

  const pct = baseline > 0 ? Math.max(0, Math.min(1, current / baseline)) : 0
  const filled = Math.round(pct * 10)
  const color = pct >= 0.85 ? 'bg-emerald-500' : pct >= 0.55 ? 'bg-amber-500' : pct >= 0.25 ? 'bg-red-500' : 'bg-red-500/40'
  const dimColor = pct >= 0.85 ? 'bg-emerald-500/20' : pct >= 0.55 ? 'bg-amber-500/20' : 'bg-red-500/20'

  return (
    <div className="flex items-center gap-1.5">
      <div className="flex gap-[2px]">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className={`w-[6px] h-3 rounded-[1px] ${i < filled ? color : dimColor}`} />
        ))}
      </div>
      <span className="text-[10px] text-term-textDim tabular-nums">{(pct * 100).toFixed(0)}%</span>
    </div>
  )
}

export function ImpactGrid({ results, currentTime }: ImpactGridProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null)

  // Sort: lost first, then by severity, then alphabetical
  const statusOrder = { MISSION_DENIED: 0, SEVERELY_DEGRADED: 1, DEGRADED: 2, NOMINAL: 3 }
  const sorted = [...results].sort((a, b) => {
    if (a.isLost !== b.isLost) return a.isLost ? -1 : 1
    const oa = statusOrder[a.status] ?? 4
    const ob = statusOrder[b.status] ?? 4
    if (oa !== ob) return oa - ob
    return a.assetType.localeCompare(b.assetType)
  })

  const impacted = sorted.filter(r => r.isLost || r.impactChains.length > 0)
  const unaffected = sorted.filter(r => !r.isLost && r.impactChains.length === 0)

  return (
    <div className="term-box flex flex-col overflow-hidden">
      <div className="px-3 pt-3 pb-2">
        <div className="text-xs text-term-textDim font-medium uppercase tracking-wider">
          Impact Assessment at T+{currentTime}min
        </div>
      </div>

      {/* Table header */}
      <div className="grid grid-cols-[1fr_100px_100px_70px_90px] gap-2 px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider text-term-textDim border-b border-term-border">
        <div>Asset</div>
        <div>Baseline</div>
        <div>Current</div>
        <div className="text-right">Delta</div>
        <div className="text-center">Status</div>
      </div>

      {/* Scrollable body */}
      <div className="overflow-auto flex-1 max-h-[400px]">
        {impacted.map(r => {
          const currentCap = r.capacityByTime[currentTime] ?? r.baselineCapacity
          const delta = r.isLost ? -100 : (r.baselineCapacity > 0
            ? ((currentCap - r.baselineCapacity) / r.baselineCapacity) * 100
            : 0)
          const isExpanded = expandedId === r.assetId
          const style = STATUS_STYLES[r.status] ?? STATUS_STYLES.NOMINAL
          const domainColor = DOMAIN_CONFIG[r.domain as keyof typeof DOMAIN_CONFIG]?.color ?? '#888'

          return (
            <div key={r.assetId}>
              <button
                onClick={() => setExpandedId(isExpanded ? null : r.assetId)}
                className={`w-full grid grid-cols-[1fr_100px_100px_70px_90px] gap-2 px-3 py-2 text-left
                           transition-colors cursor-pointer border-b border-term-border/50
                           ${isExpanded ? 'bg-term-bgLight/50' : 'hover:bg-term-bgLight/30'}
                           ${r.isLost ? 'bg-red-500/5' : ''}`}
              >
                {/* Asset name + domain */}
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: domainColor }} />
                  <div className="min-w-0">
                    <div className={`text-xs font-bold truncate ${r.isLost ? 'text-red-400 line-through' : 'text-term-text'}`}>
                      {r.assetType.split(' ').slice(0, 2).join(' ')}
                    </div>
                    <div className="text-[9px] text-term-textDim truncate">{r.role}</div>
                  </div>
                </div>

                {/* Baseline capacity */}
                <div>
                  <CapacityBar baseline={1} current={1} isLost={false} />
                  <div className="text-[9px] text-term-textDim tabular-nums">{r.baselineCapacity.toFixed(1)}%</div>
                </div>

                {/* Current capacity */}
                <div>
                  <CapacityBar baseline={r.baselineCapacity} current={currentCap} isLost={r.isLost} />
                  {!r.isLost && <div className="text-[9px] text-term-textDim tabular-nums">{currentCap.toFixed(1)}%</div>}
                </div>

                {/* Delta */}
                <div className={`text-xs font-bold tabular-nums text-right self-center
                  ${delta <= -30 ? 'text-red-400' : delta <= -10 ? 'text-amber-400' : delta < 0 ? 'text-amber-300' : 'text-term-text'}`}>
                  {r.isLost ? '—' : `${delta > 0 ? '+' : ''}${delta.toFixed(1)}%`}
                </div>

                {/* Status badge */}
                <div className="flex items-center justify-center self-center">
                  <span className={`text-[9px] font-bold px-2 py-0.5 rounded border ${style.bg} ${style.color}
                    ${r.status === 'MISSION_DENIED' ? 'animate-pulse' : ''}`}>
                    {style.label}
                  </span>
                </div>
              </button>

              {/* Expanded dependency chains */}
              {isExpanded && r.impactChains.length > 0 && (
                <div className="px-4 py-2 bg-term-bgLight/30 border-b border-term-border/50">
                  <div className="text-[9px] font-bold uppercase tracking-wider text-term-textDim mb-1.5">
                    Impact Chains ({r.impactChains.length})
                  </div>
                  <div className="space-y-1">
                    {r.impactChains.map((edge, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-[10px]">
                        <span className="text-red-400 font-bold">{edge.fromAsset.split(' ').slice(0, 2).join(' ')}</span>
                        <span className="text-term-textDim">→</span>
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase
                          ${edge.impactType.includes('denial') || edge.impactType.includes('break')
                            ? 'bg-red-500/15 text-red-400'
                            : 'bg-amber-500/15 text-amber-400'}`}>
                          {edge.impactType.replace(/_/g, ' ')}
                        </span>
                        <span className="text-term-textDim tabular-nums">r={edge.r.toFixed(2)}</span>
                        {edge.depth > 1 && (
                          <span className="text-[8px] px-1 py-0.5 rounded bg-term-bgLight text-term-textDim">
                            2nd ORDER
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        })}

        {/* Unaffected assets */}
        {unaffected.length > 0 && (
          <div className="px-3 py-2 border-t border-term-border">
            <div className="text-[9px] font-bold uppercase tracking-wider text-term-textDim mb-1">
              Unaffected ({unaffected.length})
            </div>
            <div className="flex flex-wrap gap-1">
              {unaffected.map(r => (
                <span key={r.assetId} className="text-[9px] text-term-textDim/60 px-1.5 py-0.5 rounded bg-term-bgLight/30">
                  {r.assetType.split(' ').slice(0, 2).join(' ')}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
